import { NgTemplateOutlet } from '@angular/common'
import { DOCUMENT } from '@angular/common'
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  NgZone,
  OnChanges,
  Output,
  Signal,
  signal,
  SimpleChanges,
  TemplateRef,
} from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import {
  Column,
  ColumnDef,
  ColumnSizingState,
  FilterFn,
  Cell,
  FlexRenderDirective,
  Header,
  Row,
  RowData,
  Table,
  TableFeatures,
  Updater,
  columnFacetingFeature,
  columnFilteringFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  injectTable,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_text,
  tableFeatures,
} from '@tanstack/angular-table'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { Subscription, debounceTime, filter, skip, timer } from 'rxjs'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { ListFilterValues, ListParameterQuery } from '../list-query-options'
import { SpinnerComponent } from '../spinner/spinner.component'
import { isEqual } from '../utils'
import {
  clampWidth,
  clearColumnWidths,
  DEFAULT_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  measureContentWidth,
  measureHeaderWidth,
  MIN_COLUMN_WIDTH,
  pickWidths,
  readColumnWidths,
  saveColumnWidths,
} from './table-column-widths'
import { downloadCsvFile } from './table-csv'
import { toAnchorInside, toAnchorStyle } from './table-popover-anchor'
import { ANYTIME_PRESET, ANYTIME_RANGE, CUSTOM_DATE_PRESET, DATE_PRESETS, DateFilterValue, resolveCustomRange, resolveDateRange } from './table-date-presets'
import { TableUrlParams, TableUrlSort, TableUrlState, readTableUrlParams, toQueryParams, toTableUrlParams, toTableUrlState } from './table-url-state'
import {
  AppTableEndpoint,
  AppTableFacet,
  AppTableFacetFetcher,
  AppTableFetcher,
  AppTableFetchStatus,
  AppTableSource,
  compareFacetValues,
  createFacetFetcher,
  createTableFetcher,
  isEndpointSource,
} from './table-data-source'

export { type AppTableEndpoint, type AppTableSource } from './table-data-source'
export { type TableUrlSort } from './table-url-state'
export { CUSTOM_DATE_PRESET, DATE_PRESETS, type DateFilterValue, type DatePreset, type DatePresetSort, type DateRange } from './table-date-presets'

export type AppTableTone = 'strong' | 'default' | 'muted'

/** The last three name a stage rather than a sentiment, and carry the colours the old table gave them. */
export type AppTableChipVariant = 'success' | 'danger' | 'warning' | 'neutral' | 'processing' | 'completed' | 'errored'

/** A cell shown as a coloured chip rather than as text, for a column read at a glance. */
export type AppTableChip = { label: string; variant: AppTableChipVariant }

type AppTableColumnMeta = {
  tone?: AppTableTone
  chip: ((row: unknown) => AppTableChip | null) | null
  facetLabel: ((value: string | number) => string) | null
  cellTemplate: TemplateRef<{ $implicit: unknown }> | null
  headerTemplate: TemplateRef<unknown> | null
  templateContext: Record<string, unknown> | null
  numeric?: boolean
  filter?: AppTableFilter | null
  filterTitle?: string
  filterPlaceholder?: string
  facetSearch?: boolean
}

/**
 * Column filter kind. `'facet'` lists every distinct value as a checklist, `'text'` is a single
 * substring match, `'date'` is a range preset picker.
 */
export type AppTableFilter = 'facet' | 'text' | 'date'

export type AppTableColumn<T> = {
  id: string
  /** A translation key, or a function for a header that is computed rather than translated. */
  header: string | (() => string)
  value: (row: T) => string | number
  display?: (row: T) => string
  /**
   * The name the endpoint knows this column by, used for `sortField` and the filter query keys.
   * Defaults to `id`, and only matters for an endpoint source.
   */
  field?: string
  tone?: AppTableTone
  /**
   * Shows the cell as a coloured chip instead of text. `value` still decides what the column sorts
   * and filters by, so it stays the plain reading of the cell.
   */
  chip?: (row: T) => AppTableChip | null
  /**
   * The date window the column opens on, named by preset id. The reader can change or clear it; this
   * only decides where the list starts.
   */
  defaultDatePreset?: string
  /**
   * The facet values applied when the URL says nothing about this column. Reset still clears the
   * filter; with URL sync that explicit cleared state is preserved across reloads.
   */
  defaultFacetValues?: (string | number)[]
  /** Set `false` when a facet's option list is too small to need the search input. */
  facetSearch?: boolean
  /**
   * How a facet option is worded in the picker. The values come from the database — `CREATED`, say —
   * and the reader knows them by the words the rest of the page uses. What is sent to the endpoint is
   * still the value itself.
   */
  facetLabel?: (value: string | number) => string
  /**
   * Draws the cell with the caller's own markup, for the columns that show something the table has
   * no business knowing about — avatars, badges, links. The row is the template's implicit value,
   * and `value` still decides sorting, filtering and what a download of the column says.
   */
  cellTemplate?: TemplateRef<{ $implicit: T }>
  /**
   * Extra markup under the header's own words, for a column that summarises itself — a pass rate,
   * an average and how far it has moved. The sort, filter and resize controls stay where they are;
   * this only adds to what the header says.
   */
  headerTemplate?: TemplateRef<unknown>
  /**
   * Extra values handed to this column's cell and header templates alongside the row, for a set of
   * columns built from data — one per evaluator, say — that share one template and differ only in
   * which of them is being drawn.
   */
  templateContext?: Record<string, unknown>
  numeric?: boolean
  /** Adds a sort button that cycles the rows ascending → descending → unsorted by this column. */
  sortable?: boolean
  /**
   * Column filter kind. `'facet'` shows a checklist of every distinct value and needs a facets route on
   * an endpoint source; `'text'` shows one input matching a substring (an exact number on a numeric
   * field); `'date'` shows a range preset picker (its `value` must return an epoch-ms timestamp). All
   * three are drafts until the popover's Apply button is pressed, and its Reset button clears the
   * column outright. Omit or `null` for no filter.
   */
  filter?: AppTableFilter | null
  filterTitle?: string
  filterPlaceholder?: string
  /**
   * Starting width in pixels. Omit and the column takes an equal share of the table on first render,
   * which is then pinned so the user's first drag has something to move.
   */
  width?: number
  /** Floor for both dragging and the keyboard. Defaults to 80px. */
  minWidth?: number
  /** Set `false` to drop this column's resize grip. Every column is resizable otherwise. */
  resizable?: boolean
  /**
   * Keeps the column out of the table but in the download, for a value worth having in a spreadsheet
   * and not worth a column of screen — a row's own URL, say. Such a column is never sorted, filtered
   * or resized, and it is written after the columns that are on screen.
   */
  downloadOnly?: boolean
}

/** A live resize produces a width per pointer move; only where it settles needs storing. */
const WIDTH_PERSIST_DEBOUNCE_MS = 250

/** Long enough that a word typed at speed is one request, short enough to read as it is typed. */
const TEXT_FILTER_DEBOUNCE_MS = 250

const RESIZE_STEP = 16

/** Marks the popovers that live in a `th`, which are the ones the scroll container would clip. */
const FILTER_POPOVER_PREFIX = 'filter:'
const ROW_ACTIONS_POPOVER_PREFIX = 'row-actions:'

function toIsoOrNull(time: number | null): string | null {
  return time === null ? null : new Date(time).toISOString()
}

function toTextFilterValues(value: unknown): ListFilterValues | null {
  const contains = String(value ?? '')
  return contains.length === 0 ? null : { contains }
}

function toRangeFilterValues(value: DateFilterValue | undefined): ListFilterValues | null {
  const { from, to } = value ?? ANYTIME_RANGE
  return from === null && to === null ? null : { from: toIsoOrNull(from), to: toIsoOrNull(to) }
}

function toFacetFilterValues(value: unknown): ListFilterValues | null {
  const values = (value as (string | number)[] | undefined) ?? []
  return values.length === 0 ? null : { values }
}

/** Indexes rather than rows, so `@for` has something stable to track while there is no data to key on. */
function toPlaceholderRows(count: number): number[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => index)
}

/**
 * Keeps a leading sign and a single decimal point, so a number can still be typed a character at a
 * time — `-` and `1.` are both states on the way to a value. Preferred over `type="number"`, whose
 * field reports an empty string for those same half-typed states and so cannot be filtered on.
 */
function toNumericText(text: string): string {
  const sign = text.startsWith('-') ? '-' : ''
  const [whole, ...fractions] = text.replace(/[^\d.]/g, '').split('.')
  return fractions.length === 0 ? `${sign}${whole}` : `${sign}${whole}.${fractions.join('')}`
}

// Reads the range off the filter rather than re-resolving the preset, so the rows kept here are the
// same rows the server would return for the same pick.
const dateRangeFilter: FilterFn<TableFeatures, RowData> = (row, columnId, { from, to }: DateFilterValue) => {
  const raw = row.getValue(columnId)
  const time = raw instanceof Date ? raw.getTime() : Number(raw)
  if (Number.isNaN(time)) {
    return false
  }
  return (from === null || time >= from) && (to === null || time <= to)
}

const equalsAnyFilter: FilterFn<TableFeatures, RowData> = (row, columnId, selectedValues: unknown[]) => {
  if (selectedValues.length === 0) {
    return true
  }
  const value = row.getValue(columnId)
  return selectedValues.some((selected) => selected === value)
}

// Mirrors the server's insensitive `contains`. Deliberately not matchFuzzy: a client predicate that
// accepted more than the endpoint does would make the same filter mean two different things.
const containsFilter: FilterFn<TableFeatures, RowData> = (row, columnId, search: string) =>
  String(row.getValue(columnId) ?? '')
    .toLowerCase()
    .includes(search.toLowerCase())

function toFiniteNumber(value: unknown): number | null {
  const parsed = Number(value)
  const isBlank = typeof value !== 'number' && String(value ?? '').trim().length === 0
  return isBlank || !Number.isFinite(parsed) ? null : parsed
}

// Mirrors the server's `equals` on an `Int` field, which is what it falls back to because Prisma has no
// `contains` for one. A substring match here would accept 1 for 12, which the endpoint never would.
const equalsNumberFilter: FilterFn<TableFeatures, RowData> = (row, columnId, search: string) => {
  const needle = toFiniteNumber(search)
  return needle !== null && toFiniteNumber(row.getValue(columnId)) === needle
}

const TABLE_FEATURES = tableFeatures({
  columnFacetingFeature,
  columnFilteringFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  // TanStack's own faceted row model narrows a column's facets by every other column's filter. An
  // endpoint source cannot do that, so client mode matches it instead of the two disagreeing.
  facetedRowModel: (table: Table<TableFeatures, RowData>) => () => table.getPreFilteredRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
  filteredRowModel: createFilteredRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns: { dateRange: dateRangeFilter, equalsAny: equalsAnyFilter, contains: containsFilter, equalsNumber: equalsNumberFilter },
  // `sortFn: 'auto'` silently degrades to a plain comparison unless the fn it picks is registered here.
  sortFns: { alphanumeric: sortFn_alphanumeric, text: sortFn_text },
  // Type-only slot: the value is stripped at runtime, only its type is read.
  columnMeta: {} as AppTableColumnMeta,
})

type AppTableFeatures = typeof TABLE_FEATURES

/** The registered name rather than the function: v9 resolves a column's filter through `filterFns`. */
function resolveFilterFn(filter: AppTableFilter | null, numeric: boolean): 'dateRange' | 'equalsAny' | 'contains' | 'equalsNumber' {
  if (filter === 'date') {
    return 'dateRange'
  }
  if (filter !== 'text') {
    return 'equalsAny'
  }
  return numeric ? 'equalsNumber' : 'contains'
}

export type AppTableAction<T> = {
  id: string
  label: string
  icon: string
  danger?: boolean
  /** When false, the action runs without a selection and stays enabled (e.g. "Add"). Defaults to true. */
  requiresSelection?: boolean
  confirm?: (rows: T[]) => { title: string; description: string; confirmLabel: string }
}

export type AppTableActionEvent<T> = { actionId: string; rows: T[] }

/**
 * An action belonging to one row rather than to a selection, reached through the row's own menu. The
 * menu names every action in words beside its icon, so what a row offers can be read rather than
 * recognised from a strip of icons.
 */
export type AppTableRowAction<T> = {
  id: string
  label: string
  icon: string
  danger?: boolean
  /** Rows the action does not belong to at all leave it out of their menu. */
  isVisible?: (row: T) => boolean
  /** Rows it belongs to but cannot take right now keep it, greyed out. */
  isDisabled?: (row: T) => boolean
  /** Said in place of `label` while the action is disabled, to explain why it is. */
  disabledLabel?: string
}

export type AppTableRowActionEvent<T> = { actionId: string; row: T }

/**
 * The elements that clip their own text, and so are the ones worth a tooltip. Both are block boxes,
 * which is what makes the `scrollWidth` comparison meaningful.
 */
const TRUNCATABLE_SELECTOR = 'td, .header-label'

/**
 * Recomputed on every hover rather than cached, because the same cell holds a different value after a
 * page, sort or filter change, and a stale tooltip is worse than none.
 */
function describeTruncation(event: MouseEvent) {
  const element = (event.target as HTMLElement).closest<HTMLElement>(TRUNCATABLE_SELECTOR)
  if (element === null) {
    return
  }
  const isTruncated = element.scrollWidth > element.clientWidth
  element.title = isTruncated ? (element.textContent?.trim() ?? '') : ''
}

function matchFuzzy(query: string, haystack: string): boolean {
  let matched = 0
  for (const character of haystack.toLowerCase()) {
    if (character === query[matched]) {
      matched++
    }
  }
  return matched === query.length
}

function toColumnDef<T extends RowData>(column: AppTableColumn<T>): ColumnDef<AppTableFeatures, T, unknown> {
  const filter = column.filter ?? null
  return {
    id: column.id,
    header: typeof column.header === 'string' ? column.header : column.id,
    accessorFn: column.value,
    cell: (context) => (column.display ? column.display(context.row.original) : String(context.getValue() ?? '')),
    filterFn: resolveFilterFn(filter, column.numeric === true),
    enableColumnFilter: filter !== null,
    enableSorting: column.sortable === true,
    enableResizing: column.resizable !== false,
    // Resolved here rather than through `defaultColumn`: TanStack merges a column def *over* the
    // default one, so an explicit `undefined` would clobber the default rather than defer to it.
    ...(column.width === undefined ? {} : { size: column.width }),
    minSize: column.minWidth ?? MIN_COLUMN_WIDTH,
    maxSize: MAX_COLUMN_WIDTH,
    meta: {
      tone: column.tone ?? 'default',
      chip: (column.chip as ((row: unknown) => AppTableChip | null) | undefined) ?? null,
      facetLabel: column.facetLabel ?? null,
      cellTemplate: (column.cellTemplate as TemplateRef<{ $implicit: unknown }> | undefined) ?? null,
      headerTemplate: column.headerTemplate ?? null,
      templateContext: column.templateContext ?? null,
      numeric: column.numeric === true,
      filter,
      filterTitle: column.filterTitle,
      filterPlaceholder: column.filterPlaceholder,
      facetSearch: column.facetSearch !== false,
    },
  }
}

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [NgTemplateOutlet, FlexRenderDirective, MatIcon, NgxSkeletonLoaderModule, SpinnerComponent, TranslatePipe],
  templateUrl: './table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './table.component.scss',
})
export class TableComponent<T extends RowData> implements OnChanges {
  /**
   * Either the rows themselves — paged, sorted and filtered in the browser — or an endpoint that
   * implements the list contract, in which case the server does all three.
   */
  @Input({ required: true }) source: AppTableSource<T> = []
  @Input({ required: true }) columns: AppTableColumn<T>[] = []
  @Input({ required: true }) rowId!: (row: T) => string

  @Input() pageSize = 10
  @Input() selectable = true
  @Input() highlightSelected = true
  @Input() actions: AppTableAction<T>[] = []
  @Input() rowNoun = 'TABLE.ROW'
  @Input() rowNounPlural = 'TABLE.ROWS'
  /** Accessible name for the table itself (translation key). Empty leaves the table unlabelled. */
  @Input() label = ''
  /**
   * Stable id this table's column widths are stored against, per browser. Leave it empty and resized
   * widths last only for the life of the component, as the paging does.
   */
  @Input() tableId = ''

  @Input() rowActions: AppTableRowAction<T>[] = []

  /**
   * A class put on the row itself, for a page that colours its rows by something the table cannot
   * know — a meeting's place in its lifecycle, or which row a panel beside the table is showing. The
   * class has to be styled by the page, so its stylesheet needs `::ng-deep` to reach inside here.
   */
  @Input() rowClass: ((row: T) => string | null) | null = null

  /** Names the download file and, by being set at all, offers the download. Empty offers none. */
  @Input() csvFilename = ''

  /** Where the list starts. The reader's own sorting replaces it from the first press onwards. */
  @Input() defaultSort: TableUrlSort = null

  /**
   * Runs the sorting and the filters off the URL's query parameters instead of keeping them to
   * itself, so a narrowed list can be linked to, bookmarked and reloaded, and the browser's back
   * button walks out of the page rather than back through the reader's own filters.
   *
   * What the URL says wins on the way in, and the defaults fill in only what it leaves unsaid. The
   * paging is not part of this: a sort or filter change sends the reader back to the first page
   * anyway.
   */
  @Input() syncUrl = false

  /**
   * Prefixes this table's query keys — `calls.sort`, `calls.filter.status` — which a page showing
   * more than one table needs so that two tables cannot write over each other. Null leaves them
   * bare, which is what a page with a single table wants.
   */
  @Input() urlPrefix: string | null = null

  /**
   * Says the reader chose the row itself, for a list whose rows open something — a drawer, a panel
   * beside the table. A row only becomes clickable where a page listens for this, so the tables that
   * do not are left alone.
   */
  @Output() rowActivated = new EventEmitter<T>()

  @Output() actionTriggered = new EventEmitter<AppTableActionEvent<T>>()
  @Output() rowActionTriggered = new EventEmitter<AppTableRowActionEvent<T>>()
  @Output() selectionChange = new EventEmitter<T[]>()

  readonly actionsPopover = 'actions'
  readonly pageSizeOptions = [10, 25, 50, 100]

  readonly maxColumnWidth = MAX_COLUMN_WIDTH
  readonly resizeStep = RESIZE_STEP

  /** Sized to the cell rather than the library's own block, which comes with a margin and a height. */
  readonly skeletonTheme = { height: '12px', margin: '0', 'border-radius': '4px' }

  private readonly dialog = inject(MatDialog)
  private readonly destroyRef = inject(DestroyRef)
  private readonly translateService = inject(TranslateService)
  private readonly document = inject(DOCUMENT)
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly injector = inject(Injector)
  private readonly zone = inject(NgZone)
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)

  private unwatchAnchor: (() => void) | null = null

  private openPopover: string | null = null
  /**
   * A header popover is `position: fixed` so the table's horizontal scroll container can't clip it,
   * which also takes it out of the table's flow — so it is placed against the rect of whatever opened
   * it, and held inside the table's own rect from there.
   */
  popoverStyle: Record<string, string> | null = null
  optionSearch = ''
  /** What the popovers are showing; nothing here filters a row or reaches the server until Apply is pressed. */
  textSearch = ''
  datePreset = ANYTIME_PRESET
  private optionDraft = new Set<unknown>()
  /** The control that opened the current popover, so focus can return to it on close. */
  private popoverTrigger: HTMLElement | null = null

  // TanStack Angular Table drives its row models through signals, so the inputs it
  // consumes are mirrored into signals here; the parent-facing API stays @Input/@Output.
  private readonly sourceSignal = signal<AppTableSource<T>>([])
  private readonly columnsSignal = signal<AppTableColumn<T>[]>([])
  private readonly rowIdSignal = signal<(row: T) => string>(() => '')
  private readonly selectableSignal = signal(true)

  /**
   * Column widths are controlled here rather than left to TanStack, and that is load-bearing rather
   * than stylistic. `getResizeHandler` calls `setColumnSizingInfo` and `setColumnSizing` back to back
   * on every pointer move, and the Angular adapter hands both setters the same captured state, so the
   * second discards the first (the same hazard `resetPageAfter` works around). Owning this half of the
   * state sends the two writes to two different stores, so neither is lost.
   */
  private readonly columnSizing = signal<ColumnSizingState>({})

  /** Seeded widths are the browser's own layout, so only the columns the user dragged are worth storing. */
  private readonly resizedColumnIds = new Set<string>()

  /** Restored widths are a choice the user already made, so they outrank a re-measure the way a drag does. */
  private hasStoredWidths = false

  private get hasResized(): boolean {
    return this.resizedColumnIds.size > 0
  }

  private containerWidth = 0

  private readonly renderedColumns = computed(() => this.columnsSignal().filter((column) => column.downloadOnly !== true))

  private readonly downloadOnlyColumns = computed(() => this.columnsSignal().filter((column) => column.downloadOnly === true))

  private readonly columnDefs = computed(() => this.renderedColumns().map(toColumnDef))

  private readonly columnsById = computed(() => new Map(this.columnsSignal().map((column) => [column.id, column])))

  private readonly endpoint = computed<AppTableEndpoint | null>(() => {
    const source = this.sourceSignal()
    return isEndpointSource(source) ? source : null
  })

  private readonly arrayRows = computed<T[]>(() => {
    const source = this.sourceSignal()
    return isEndpointSource(source) ? [] : source
  })

  // Every table state change produces a fresh state object, so both of these need value equality or a
  // row-selection click would look like a new request and refetch the page.
  private readonly listQuery = computed<ListParameterQuery>(
    () => {
      const pagination = this.table.atoms.pagination.get()
      const sorting = this.table.atoms.sorting.get()
      const [sort] = sorting
      const sortField = sort === undefined ? undefined : this.fieldFor(sort.id)
      return {
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        ...(sortField === undefined ? {} : { sortField, sortDirection: sort.desc ? ('desc' as const) : ('asc' as const) }),
      }
    },
    { equal: isEqual }
  )

  private readonly serverFilters = computed<Record<string, ListFilterValues>>(
    () =>
      this.table.atoms.columnFilters.get().reduce<Record<string, ListFilterValues>>((filters, { id, value }) => {
        const values = this.toFilterValues(id, value)
        return values === null ? filters : { ...filters, [this.fieldFor(id)]: values }
      }, {}),
    { equal: isEqual }
  )

  private readonly fetcher: AppTableFetcher<T> = createTableFetcher<T>(this.endpoint, this.listQuery, this.serverFilters)
  private readonly facetFetcher: AppTableFacetFetcher = createFacetFetcher()

  // Annotated because the options callback reads the fetch's total while the fetch is driven by this
  // table's state, and TypeScript cannot infer its way around that.
  readonly table = injectTable(() => {
    const rowId = this.rowIdSignal()
    const isServerDriven = this.endpoint() !== null
    return {
      features: TABLE_FEATURES,
      data: isServerDriven ? this.fetcher.rows() : this.arrayRows(),
      columns: this.columnDefs(),
      getRowId: (row: T) => rowId(row),
      initialState: { pagination: { pageIndex: 0, pageSize: this.pageSize } },
      enableRowSelection: this.selectableSignal(),
      enableColumnResizing: true,
      // Must stay 'onChange'. TanStack's default 'onEnd' commits the new width in the same block that
      // clears the resize info, and the adapter drops the earlier of two setters — so the column would
      // snap back on release. 'onChange' has already committed the width by then.
      columnResizeMode: 'onChange' as const,
      state: { columnSizing: this.columnSizing() },
      onColumnSizingChange: (updater: Updater<ColumnSizingState>) => this.resizeColumns(updater),
      // The row models are registered on the features, so a server-driven table turns them off by
      // hand rather than by leaving them out.
      manualPagination: isServerDriven,
      manualSorting: isServerDriven,
      manualFiltering: isServerDriven,
      ...(isServerDriven
        ? {
            rowCount: this.fetcher.total(),
            // The page is reset explicitly on sort and filter changes; leaving it automatic would
            // reset it on every arriving page as well.
            autoResetPageIndex: false,
          }
        : {}),
    }
  })

  readonly selectedRows = computed(() => this.table.getSelectedRowModel().rows.map((row: Row<AppTableFeatures, T>) => row.original))

  constructor() {
    toObservable(this.selectedRows)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => this.selectionChange.emit(rows))

    // getSelectedRowModel() only sees loaded rows, so a selection that survived a refetch would
    // silently under-report anything selected on a page that is no longer loaded.
    toObservable(this.fetcher.loadCount)
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (Object.keys(this.table.atoms.rowSelection.get()).length > 0) {
          this.table.resetRowSelection()
        }
      })

    // A live drag writes a width per pointer move, so the storage write is paced rather than made
    // once per pixel.
    toObservable(this.columnSizing)
      .pipe(
        filter(() => this.hasResized),
        debounceTime(WIDTH_PERSIST_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((widths) => saveColumnWidths(this.tableId, pickWidths(widths, this.resizedColumnIds)))

    this.seedColumnWidths()
    this.watchContainerWidth()
    this.watchTruncation()
    this.destroyRef.onDestroy(() => this.unwatchAnchor?.())
  }

  /**
   * A cell whose text no longer fits gets the full value as its tooltip — which a narrow column now
   * makes common. Delegated to one listener outside Angular, and resolved on hover rather than bound
   * per cell: a title binding on every cell of a hundred-row page would be re-checked on every drag
   * frame, to show something nobody is looking at.
   */
  private watchTruncation() {
    const host = this.host.nativeElement
    this.zone.runOutsideAngular(() => host.addEventListener('mouseover', describeTruncation))
    this.destroyRef.onDestroy(() => host.removeEventListener('mouseover', describeTruncation))
  }

  /**
   * TanStack has no notion of an unsized column — `getSize()` always answers, falling back to 150px —
   * so the equal shares the browser worked out on the first render are measured and pinned. The numbers
   * are the widths already on screen, so nothing moves; the table only stops being implicitly sized.
   */
  private seedColumnWidths() {
    afterNextRender(
      () => {
        const rendered = this.renderedWidths()
        this.columnSizing.set(
          Object.fromEntries(
            this.table.getVisibleLeafColumns().map((column) => [column.id, this.columnSizing()[column.id] ?? rendered.get(column.id) ?? DEFAULT_COLUMN_WIDTH])
          )
        )
      },
      { injector: this.injector }
    )
  }

  /**
   * Pinning widths costs the table its fluidity, so a table nobody has resized is re-measured when its
   * container changes size — otherwise every table in the app would freeze at whatever width the window
   * happened to be the first time it rendered.
   */
  private watchContainerWidth() {
    const observer = new ResizeObserver((entries) => this.zone.run(() => this.reseedFor(entries[0].contentRect.width)))
    afterNextRender(
      () => {
        const container = this.host.nativeElement.querySelector('.table-scroll')
        if (container !== null) {
          observer.observe(container)
        }
      },
      { injector: this.injector }
    )
    this.destroyRef.onDestroy(() => observer.disconnect())
  }

  /** Seeded widths were measured from the columns that were on screen, so a new set measures its own. */
  /**
   * Where the list opens: what the URL asks for where the table is run off it, and the starting sort,
   * date window and facet values for whatever it leaves unsaid. Only ever on the way in — a reader
   * who has since sorted or filtered keeps what they chose.
   */
  private applyInitialState() {
    // A page whose columns arrive from a stream starts with none, and marking this done against that
    // empty first pass would leave the real columns without their defaults.
    if (this.hasAppliedInitialState || this.columns.length === 0) {
      return
    }
    this.hasAppliedInitialState = true

    this.applyParams(this.syncUrl ? readTableUrlParams(this.route.snapshot.queryParamMap, this.urlPrefix, this.columns) : {})

    if (this.syncUrl) {
      this.watchUrl()
    }
  }

  private hasAppliedInitialState = false

  /**
   * Puts the table where a set of query parameters says it should be. A whole replacement rather than
   * a merge, because that is what the URL is: a key it leaves out means the column's declared default
   * (or no filter where it has none), not that whatever is there should stay.
   */
  private applyParams(params: TableUrlParams) {
    this.lastSyncedParams = params
    const { sorting, filters, statedWindow } = toTableUrlState(params, this.urlPrefix, this.columns, this.defaultSort, Date.now())
    // One `resetPageAfter` each rather than both changes in one: two state setters in a row lose the
    // first, and the page check in between is the read that re-syncs the table.
    this.resetPageAfter(() => this.table.setSorting(sorting))
    this.resetPageAfter(() => this.table.setColumnFilters(filters))
    this.customFrom = statedWindow.from
    this.customTo = statedWindow.to
  }

  /** The half of the table's state the URL carries, in the shape the table itself holds it. */
  private readonly urlSyncedState = computed<Pick<TableUrlState, 'sorting' | 'filters'>>(
    () => ({ sorting: this.table.atoms.sorting.get(), filters: this.table.atoms.columnFilters.get() }),
    { equal: isEqual }
  )

  /** What the URL and the table last agreed on, so that neither side answers its own writing. */
  private lastSyncedParams: TableUrlParams = {}

  /**
   * Both directions, so a link, a reload and the back button all land where they say they will.
   *
   * The two sides are reconciled in the URL's terms rather than the table's, which is what makes it
   * settle: a relative window such as "last 7 days" resolves against the clock every time it is
   * read, so the same link read twice is two different pairs of instants and comparing table state
   * would leave the two writing over each other for ever.
   */
  private watchUrl() {
    toObservable(this.urlSyncedState, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.writeUrl())
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((paramMap) => this.readUrl(paramMap))
  }

  private writeUrl() {
    const params = toTableUrlParams(this.urlSyncedState(), this.urlPrefix, this.columns, this.defaultSort)
    if (isEqual(params, this.lastSyncedParams)) {
      return
    }
    this.lastSyncedParams = params
    // Replaced rather than pushed: a filter takes hold as it is set, so pushing would bury the page
    // the reader arrived from under an entry per keystroke.
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: toQueryParams(params, this.urlPrefix, this.columns),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  private readUrl(paramMap: ParamMap) {
    const params = readTableUrlParams(paramMap, this.urlPrefix, this.columns)
    if (isEqual(params, this.lastSyncedParams)) {
      return
    }
    this.applyParams(params)
  }

  private reseedForColumns(isFirstChange: boolean) {
    if (isFirstChange || this.hasResized || this.hasStoredWidths) {
      return
    }
    this.columnSizing.set({})
    this.seedColumnWidths()
  }

  private reseedFor(width: number) {
    // The observer also reports height changes, which rows coming and going produce constantly.
    const isFirstMeasurement = this.containerWidth === 0
    if (width === this.containerWidth) {
      return
    }
    this.containerWidth = width
    if (isFirstMeasurement || this.hasResized || this.hasStoredWidths) {
      return
    }
    this.columnSizing.set({})
    this.seedColumnWidths()
  }

  /** One pass over the headers rather than a subtree scan per column; empty while the table is off-screen. */
  private renderedWidths(): Map<string, number> {
    const headers = this.host.nativeElement.querySelectorAll<HTMLElement>('thead th[data-column-id]')
    return new Map(
      Array.from(headers)
        .filter((header) => header.offsetWidth > 0)
        .map((header) => [header.dataset['columnId'] ?? '', header.offsetWidth])
    )
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['source']) {
      this.facetFetcher.clearFacets()
      this.sourceSignal.set(this.source)
    }
    if (changes['columns']) {
      this.columnsSignal.set(this.columns)
      this.reseedForColumns(changes['columns'].isFirstChange())
    }
    // Read before the first render, so a stored width wins and the seed only fills in the rest.
    if (changes['tableId']) {
      const stored = readColumnWidths(this.tableId, new Set(this.columns.map((column) => column.id)))
      if (Object.keys(stored).length > 0) {
        this.hasStoredWidths = true
        this.columnSizing.update((widths) => ({ ...widths, ...stored }))
      }
    }
    if (changes['rowId']) {
      this.rowIdSignal.set(this.rowId)
    }
    if (changes['selectable']) {
      this.selectableSignal.set(this.selectable)
    }
    if (changes['pageSize'] && !changes['pageSize'].isFirstChange()) {
      this.table.setPageSize(this.pageSize)
    }
    // Last, and not from inside the `columns` branch: this is the first thing to write table state,
    // which builds the row model — and the row model keys its rows by `getRowId` once and keeps them,
    // so doing it before `rowId` has been mirrored would leave every row sharing an empty id.
    this.applyInitialState()
  }

  private fieldFor(columnId: string): string {
    return this.columnsById().get(columnId)?.field ?? columnId
  }

  private filterModeFor(columnId: string): AppTableFilter | null {
    return this.columnsById().get(columnId)?.filter ?? null
  }

  /** Null for a filter that would match everything, so it is left off the request entirely. */
  private toFilterValues(columnId: string, value: unknown): ListFilterValues | null {
    switch (this.filterModeFor(columnId)) {
      case 'text':
        return toTextFilterValues(value)
      case 'date':
        return toRangeFilterValues(value as DateFilterValue | undefined)
      default:
        return toFacetFilterValues(value)
    }
  }

  get isServerDriven(): boolean {
    return this.endpoint() !== null
  }

  get isLoading(): boolean {
    return this.isServerDriven && this.fetcher.status() === 'loading'
  }

  get isError(): boolean {
    return this.isServerDriven && this.fetcher.status() === 'error'
  }

  /** A page is on screen while the next one loads, so it is dimmed rather than replaced. */
  get isRefetching(): boolean {
    return this.isServerDriven && this.fetcher.status() === 'success' && this.fetcher.isFetching()
  }

  retry() {
    this.fetcher.retry()
  }

  get selectedCount(): number {
    return this.selectedRows().length
  }

  /** A whole page of them, so the table is already the height the rows will need when they land. */
  get skeletonRows(): number[] {
    return toPlaceholderRows(this.table.atoms.pagination.get().pageSize)
  }

  /** Includes the trailing spacer column, which every full-width row has to span. */
  get columnCount(): number {
    return this.table.getVisibleLeafColumns().length + (this.selectable ? 1 : 0) + (this.hasRowActions ? 1 : 0) + 1
  }

  /**
   * Null until this column has a width of its own, which leaves the browser sharing the table out
   * equally. Read through `getSize()` rather than off the sizing map, because TanStack writes raw
   * pointer deltas into that map and only applies the column's min and max on the way out.
   */
  columnWidth(header: Header<AppTableFeatures, T, unknown>): number | null {
    return header.column.id in this.columnSizing() ? header.getSize() : (this.columnsById().get(header.column.id)?.width ?? null)
  }

  get isResizing(): boolean {
    return this.table.atoms.columnResizing.get().isResizingColumn !== false
  }

  /**
   * A grip can be pressed without being dragged, and TanStack writes the sizing either way. Only a
   * width that actually moved counts as the user's, so a stray click doesn't pin the browser's own.
   */
  private resizeColumns(updater: Updater<ColumnSizingState>) {
    const current = this.columnSizing()
    const next = updater instanceof Function ? updater(current) : updater
    if (isEqual(next, current)) {
      return
    }
    Object.keys(next)
      .filter((columnId) => next[columnId] !== current[columnId])
      .forEach((columnId) => this.resizedColumnIds.add(columnId))
    this.columnSizing.set(next)
  }

  startResize(header: Header<AppTableFeatures, T, unknown>, event: MouseEvent | TouchEvent) {
    header.getResizeHandler()(event)
  }

  minWidthFor(column: Column<AppTableFeatures, T, unknown>): number {
    return column.columnDef.minSize ?? MIN_COLUMN_WIDTH
  }

  nudgeWidth(column: Column<AppTableFeatures, T, unknown>, delta: number, event: Event) {
    this.setWidth(column, column.getSize() + delta, event)
  }

  setWidth(column: Column<AppTableFeatures, T, unknown>, width: number, event: Event) {
    // Otherwise the arrow and Home/End keys scroll the table out from under the grip being used.
    event.preventDefault()
    const clamped = clampWidth(width, this.minWidthFor(column), MAX_COLUMN_WIDTH)
    this.table.setColumnSizing((widths) => ({ ...widths, [column.id]: clamped }))
  }

  /**
   * Sizes a column to the widest value it is currently showing. Only the rendered page can be measured —
   * for an endpoint source the rest of the rows aren't in the browser to measure.
   */
  autoFitColumn(column: Column<AppTableFeatures, T, unknown>, event: Event) {
    // Positional rather than an attribute per cell: the column is the same for every row, so tagging
    // each one would add a binding per cell for change detection to re-check on every drag frame.
    const cellIndex = this.table.getVisibleLeafColumns().indexOf(column) + (this.selectable ? 2 : 1)
    const cells = this.host.nativeElement.querySelectorAll<HTMLElement>(`tbody tr:not(.status-row, .skeleton-row) td:nth-child(${cellIndex})`)
    const header = this.host.nativeElement.querySelector<HTMLElement>(`th[data-column-id="${column.id}"]`)
    this.setWidth(column, Math.max(measureContentWidth(Array.from(cells)), measureHeaderWidth(header)), event)
  }

  resetColumnWidths() {
    clearColumnWidths(this.tableId)
    this.resizedColumnIds.clear()
    this.hasStoredWidths = false
    this.columnSizing.set({})
    this.seedColumnWidths()
    this.closePopover()
  }

  /** The footer stays put while rows load — only its counts wait, as skeletons, for numbers to report. */
  get showPagination(): boolean {
    return !this.isError
  }

  get currentPageIndex(): number {
    return this.table.atoms.pagination.get().pageIndex
  }

  get currentPageSize(): number {
    return this.table.atoms.pagination.get().pageSize
  }

  isPageSize(size: number): boolean {
    return this.currentPageSize === size
  }

  setPageSize(size: number) {
    this.table.setPageSize(size)
  }

  get paginationSummary(): string {
    const total = this.table.getRowCount()
    const { pageIndex, pageSize } = this.table.atoms.pagination.get()
    const start = total === 0 ? 0 : pageIndex * pageSize + 1
    const noun = this.translateService.instant(total === 1 ? this.rowNoun : this.rowNounPlural)
    return this.translateService.instant('TABLE.PAGINATION_SUMMARY', { start, end: Math.min(start + pageSize - 1, total), total, noun })
  }

  /** The column whose filter popover is open, if any. Only one popover is open at a time. */
  private get filteredColumn(): Column<AppTableFeatures, T, unknown> | null {
    const key = this.openPopover
    return key?.startsWith(FILTER_POPOVER_PREFIX) === true ? (this.table.getColumn(key.slice(FILTER_POPOVER_PREFIX.length)) ?? null) : null
  }

  get filterOptions(): unknown[] {
    const column = this.filteredColumn
    if (column === null) {
      return []
    }
    const query = this.optionSearch.toLowerCase().trim()
    const options = this.facetValues(column)
    return query.length === 0 ? options : options.filter((option) => matchFuzzy(query, String(option)))
  }

  private facetValues(column: Column<AppTableFeatures, T, unknown>): unknown[] {
    // Fetched facets arrive sorted; a client facet is sorted here because its values come off the rows.
    return this.openFacet === null ? [...column.getFacetedUniqueValues().keys()].sort(compareFacetValues) : this.openFacet.values
  }

  /** The fetched facet for the open popover's column, or null for an array source. */
  private get openFacet(): AppTableFacet | null {
    const column = this.filteredColumn
    if (column === null || !this.isServerDriven) {
      return null
    }
    return this.facetFetcher.facets()[this.fieldFor(column.id)] ?? { status: 'loading', values: [] }
  }

  /** Null for an array source, whose options need no fetching. */
  get facetStatus(): AppTableFetchStatus | null {
    return this.openFacet?.status ?? null
  }

  get selectedOptionCount(): number {
    return this.filterOptions.filter((option) => this.optionDraft.has(option)).length
  }

  /** A column with no values at all is not the same as a search that matched none of them. */
  get hasOptionSearch(): boolean {
    return this.optionSearch.trim().length > 0
  }

  get showSelectOptionsButton(): boolean {
    return this.hasOptionSearch && this.filterOptions.length > 0
  }

  isPopoverOpen(key: string): boolean {
    return this.openPopover === key
  }

  /**
   * The words in the header, ready to show. A column names itself with a translation key, or with a
   * function when the label is computed — an interval's month, say — and has nothing to translate.
   */
  headerLabel(column: Column<AppTableFeatures, T, unknown>): string {
    const definition = this.columnsById().get(column.id) ?? null
    return definition === null ? this.translateService.instant(column.id) : this.columnHeaderLabel(definition)
  }

  private columnHeaderLabel(column: AppTableColumn<T>): string {
    return typeof column.header === 'function' ? column.header() : this.translateService.instant(column.header)
  }

  filterTitle(column: Column<AppTableFeatures, T, unknown>): string {
    return column.columnDef.meta?.filterTitle ?? this.translateColumnLabel('TABLE.FILTER_BY', column)
  }

  filterPlaceholder(column: Column<AppTableFeatures, T, unknown>): string {
    const fallbackKey = column.columnDef.meta?.filter === 'text' ? 'TABLE.MATCH_COLUMN' : 'TABLE.SEARCH_COLUMN'
    return column.columnDef.meta?.filterPlaceholder ?? this.translateColumnLabel(fallbackKey, column)
  }

  private translateColumnLabel(key: string, column: Column<AppTableFeatures, T, unknown>): string {
    return this.translateService.instant(key, { column: this.headerLabel(column) })
  }

  filterPopoverKey(column: Column<AppTableFeatures, T, unknown>): string {
    return `${FILTER_POPOVER_PREFIX}${column.id}`
  }

  /** Facet and text modes share the popover chrome and its search box; date gets its own. */
  isValueFilter(column: Column<AppTableFeatures, T, unknown>): boolean {
    const filter = column.columnDef.meta?.filter
    return filter === 'facet' || filter === 'text'
  }

  isTextFilter(column: Column<AppTableFeatures, T, unknown>): boolean {
    return column.columnDef.meta?.filter === 'text'
  }

  isFacetFilter(column: Column<AppTableFeatures, T, unknown>): boolean {
    return column.columnDef.meta?.filter === 'facet'
  }

  /** The words for one facet option. The value itself is what the filter still carries. */
  facetOptionLabel(column: Column<AppTableFeatures, T, unknown>, value: string | number): string {
    const label = column.columnDef.meta?.facetLabel ?? null
    return label === null ? String(value) : label(value)
  }

  /** Enter and the Apply button reach the same place, whichever value filter the popover is showing. */
  applyValueFilter(column: Column<AppTableFeatures, T, unknown>) {
    if (this.isTextFilter(column)) {
      this.applyTextFilter(column)
      return
    }
    this.applyFacetFilter(column)
  }

  togglePopover(key: string) {
    this.optionSearch = ''
    if (this.openPopover === key) {
      this.closePopover()
      return
    }
    this.popoverTrigger = this.document.activeElement as HTMLElement | null
    this.openPopover = key
    this.anchorPopover(key)
    this.prepareFilterPopover()
  }

  /**
   * Only the header popovers are placed in viewport coordinates — the card's own sit outside the
   * scroll container and are still positioned against it. A fixed popover cannot follow its trigger, so
   * while one is open the page is watched and it is dismissed rather than left floating.
   */
  private anchorPopover(key: string) {
    const isAnchoredToACell = key.startsWith(FILTER_POPOVER_PREFIX) || key.startsWith(ROW_ACTIONS_POPOVER_PREFIX)
    const trigger = isAnchoredToACell ? (this.popoverTrigger?.getBoundingClientRect() ?? null) : null
    if (trigger === null) {
      return
    }
    const table = this.host.nativeElement.getBoundingClientRect()
    this.popoverStyle = toAnchorStyle(toAnchorInside(trigger, table, this.measureViewport()))
    this.watchAnchor()
  }

  /**
   * The box a `position: fixed` offset is measured from, which `window.innerWidth` is not — it counts
   * the scrollbar, and placing against it puts the popover a scrollbar's width off.
   */
  private measureViewport(): { width: number; height: number } {
    const { clientWidth, clientHeight } = this.document.documentElement
    return { width: clientWidth, height: clientHeight }
  }

  /**
   * Registered only while an anchored popover is open, and outside Angular so that ordinary scrolling
   * doesn't tick the whole app. Capture catches the table's own scroll container as well as the page.
   */
  private watchAnchor() {
    // Opening a second popover re-anchors without closing the first, which would leave its pair of
    // listeners on the window with no one holding the remover.
    this.unwatchAnchor?.()
    const dismiss = () => this.zone.run(() => this.closePopover())
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', dismiss, { capture: true, passive: true })
      window.addEventListener('resize', dismiss, { passive: true })
    })
    this.unwatchAnchor = () => {
      window.removeEventListener('scroll', dismiss, { capture: true })
      window.removeEventListener('resize', dismiss)
    }
  }

  /**
   * `autofocus` only counts on the first render of a document, so a popover opened later has to ask
   * for the caret itself. Deferred a frame because the input does not exist until this render lands.
   */
  private focusFilterInput() {
    afterNextRender(
      () => {
        const input = this.host.nativeElement.querySelector<HTMLInputElement>('.filter-popover input')
        input?.select()
      },
      { injector: this.injector }
    )
  }

  private prepareFilterPopover() {
    const column = this.filteredColumn
    if (column === null) {
      return
    }
    if (this.filterModeFor(column.id) !== 'date') {
      this.focusFilterInput()
    }
    const mode = this.filterModeFor(column.id)
    if (mode === 'text') {
      this.textSearch = (column.getFilterValue() as string | undefined) ?? ''
      return
    }
    if (mode === 'date') {
      this.datePreset = this.selectedDatePreset(column)
      return
    }
    this.optionDraft = new Set(column.getFilterValue() as unknown[] | undefined)
    const endpoint = this.endpoint()
    if (mode === 'facet' && endpoint !== null) {
      this.facetFetcher.loadFacet(endpoint, this.fieldFor(column.id))
    }
  }

  closePopover() {
    if (this.openPopover === null) {
      return
    }
    this.openPopover = null
    this.popoverStyle = null
    this.unwatchAnchor?.()
    this.unwatchAnchor = null
    this.popoverTrigger?.focus()
    this.popoverTrigger = null
  }

  get anyPopoverOpen(): boolean {
    return this.openPopover !== null
  }

  isNumericColumn(column: Column<AppTableFeatures, T, unknown>): boolean {
    return column.columnDef.meta?.numeric === true
  }

  /**
   * Writes the element back rather than only the bound field: a rejected keystroke leaves the field
   * holding what it already held, and `[value]` repaints nothing when its expression has not changed.
   */
  private keepNumbersOnly(input: HTMLInputElement): string {
    const numbers = toNumericText(input.value)
    if (numbers === input.value) {
      return numbers
    }
    // Counting what survives ahead of the caret keeps it where the user left it, instead of the jump
    // to the end that reassigning `value` would otherwise cause mid-edit.
    const caret = toNumericText(input.value.slice(0, input.selectionStart ?? input.value.length)).length
    input.value = numbers
    input.setSelectionRange(caret, caret)
    return numbers
  }

  /** A text filter searches the rows themselves; the other value filters search their own option list. */
  searchValue(column: Column<AppTableFeatures, T, unknown>): string {
    return this.isTextFilter(column) ? this.textSearch : this.optionSearch
  }

  search(column: Column<AppTableFeatures, T, unknown>, event: Event) {
    const input = event.target as HTMLInputElement
    const search = this.isNumericColumn(column) ? this.keepNumbersOnly(input) : input.value
    if (!this.isTextFilter(column)) {
      this.optionSearch = search
      return
    }
    this.textSearch = search
  }

  /**
   * TanStack's Angular adapter binds each state setter to the state the table last read, so two
   * setters in a row make the second discard the first. Reading the state in between — which the page
   * check below does — re-syncs the table with the pending change.
   */
  private resetPageAfter(change: () => void) {
    change()
    if (this.table.atoms.pagination.get().pageIndex !== 0) {
      this.table.setPageIndex(0)
    }
  }

  applyTextFilter(column: Column<AppTableFeatures, T, unknown>, close = true) {
    // Trimmed at the one write site, so the client predicate and the server's `contains` see the same
    // needle.
    const search = this.textSearch.trim()
    this.commitFilter(column, search.length === 0 ? undefined : search, close)
  }

  /** Clears the column rather than the draft, so removing a filter takes one press and not two. */
  resetFilter(column: Column<AppTableFeatures, T, unknown>) {
    this.commitFilter(column, undefined)
  }

  /**
   * Closing is for the presses that say "done" — Enter, Reset. A filter that takes hold as it is set
   * must leave the popover where it is, or the second character of a search would be typed into a
   * box that had already gone, and a second option could never be ticked.
   */
  private commitFilter(column: Column<AppTableFeatures, T, unknown>, value: unknown, close = true) {
    this.resetPageAfter(() => column.setFilterValue(value))
    if (close) {
      this.closePopover()
    }
  }

  isOptionSelected(option: unknown): boolean {
    return this.optionDraft.has(option)
  }

  toggleOption(option: unknown) {
    const selected = new Set(this.optionDraft)
    selected.has(option) ? selected.delete(option) : selected.add(option)
    this.optionDraft = selected
  }

  selectOptions() {
    this.optionDraft = new Set([...this.optionDraft, ...this.filterOptions])
  }

  unselectOptions() {
    const selected = new Set(this.optionDraft)
    this.filterOptions.forEach((option) => selected.delete(option))
    this.optionDraft = selected
  }

  applyFacetFilter(column: Column<AppTableFeatures, T, unknown>, close = true) {
    this.commitFilter(column, this.optionDraft.size === 0 ? undefined : [...this.optionDraft], close)
  }

  /**
   * The filters take hold as they are set rather than on a button press. A typed one is held back
   * briefly so that a request does not go out per keystroke; the others land at once, since a tick
   * or a preset is a whole decision on its own.
   */
  onTextFilterInput(column: Column<AppTableFeatures, T, unknown>) {
    this.textFilterDebounce?.unsubscribe()
    this.textFilterDebounce = timer(TEXT_FILTER_DEBOUNCE_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyTextFilter(column, false))
  }

  onFacetOptionToggled(column: Column<AppTableFeatures, T, unknown>) {
    this.applyFacetFilter(column, false)
  }

  activeFilterCount(column: Column<AppTableFeatures, T, unknown>): number {
    return ((column.getFilterValue() as unknown[] | undefined) ?? []).length
  }

  private textFilterDebounce: Subscription | null = null

  readonly datePresets = DATE_PRESETS

  toggleSorting(column: Column<AppTableFeatures, T, unknown>) {
    this.resetPageAfter(() => column.toggleSorting())
  }

  sortIcon(column: Column<AppTableFeatures, T, unknown>): string {
    const sorted = column.getIsSorted()
    return sorted === 'asc' ? 'arrow_upward' : sorted === 'desc' ? 'arrow_downward' : 'swap_vert'
  }

  sortAriaState(column: Column<AppTableFeatures, T, unknown>): 'ascending' | 'descending' | 'none' {
    const sorted = column.getIsSorted()
    return sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'
  }

  selectedDatePreset(column: Column<AppTableFeatures, T, unknown>): string {
    return (column.getFilterValue() as DateFilterValue | undefined)?.presetId ?? ANYTIME_PRESET
  }

  isDatePresetSelected(presetId: string): boolean {
    return this.datePreset === presetId
  }

  readonly customDatePreset = CUSTOM_DATE_PRESET

  /** The two ends of a stated window, as the date inputs hold them: `yyyy-mm-dd` or empty. */
  customFrom = ''
  customTo = ''

  get isCustomDateRange(): boolean {
    return this.datePreset === CUSTOM_DATE_PRESET
  }

  setCustomDateEnd(end: 'from' | 'to', event: Event, column: Column<AppTableFeatures, T, unknown>) {
    const value = (event.target as HTMLInputElement).value
    if (end === 'from') {
      this.customFrom = value
    } else {
      this.customTo = value
    }
    this.applyDateFilter(column, false)
  }

  setDatePreset(presetId: string, column: Column<AppTableFeatures, T, unknown>) {
    this.datePreset = presetId
    this.applyDateFilter(column, false)

    // A window that knows which end matters turns the column round to face it.
    const sort = DATE_PRESETS.find((preset) => preset.id === presetId)?.sort
    if (sort !== undefined && column.getCanSort()) {
      this.resetPageAfter(() => this.table.setSorting([{ id: column.id, desc: sort === 'desc' }]))
    }
  }

  /** Resolved here rather than when the preset was picked, so the window is the one that was asked for. */
  applyDateFilter(column: Column<AppTableFeatures, T, unknown>, close = true) {
    const resolved = this.isCustomDateRange ? resolveCustomRange(this.customFrom, this.customTo) : resolveDateRange(this.datePreset, Date.now())
    const range: DateFilterValue = { presetId: this.datePreset, ...resolved }
    const isEmpty = this.datePreset === ANYTIME_PRESET || (resolved.from === null && resolved.to === null)
    this.commitFilter(column, isEmpty ? undefined : range, close)
  }

  actionScopedToSelection(action: AppTableAction<T>): boolean {
    return action.requiresSelection !== false
  }

  get hasRowActions(): boolean {
    return this.rowActions.length > 0
  }

  /**
   * Only worth offering once there is something to undo, which is also when the row appears.
   *
   * Read from the filter state rather than from the columns on screen, because that is the state the
   * endpoint query is built from and the state this clears. A filter whose column has since gone —
   * hidden, or dropped from the columns input — still narrows the rows, so the way to clear it has to
   * stay offered or the reader is left filtered by something they cannot see or reach.
   */
  get hasAnyFilter(): boolean {
    return this.table.atoms.columnFilters.get().length > 0
  }

  clearAllFilters() {
    this.closePopover()
    this.resetPageAfter(() => this.table.setColumnFilters([]))
  }

  get canDownloadCsv(): boolean {
    return this.csvFilename !== '' && this.table.getRowModel().rows.length > 0
  }

  /**
   * Downloads what is on screen: the rows as filtered and sorted, read the way the table reads them.
   * An endpoint-backed table holds one page at a time, so that is what a download of one contains,
   * which is what the old table did too.
   */
  downloadCsv() {
    const columns = this.columnsById()
    const onScreen = this.table.getVisibleLeafColumns().map((column) => columns.get(column.id) ?? null)
    const written = [...onScreen, ...this.downloadOnlyColumns()]
    const headers = written.map((column) => (column === null ? '' : this.columnHeaderLabel(column)))
    const rows = this.table
      .getRowModel()
      .rows.map((row) => written.map((column) => (column === null ? '' : String(column.display?.(row.original) ?? column.value(row.original) ?? ''))))
    downloadCsvFile([headers, ...rows], `${this.csvFilename}.csv`, this.document)
  }

  /**
   * What a cell template is given: the row, plus whatever the column carries for the templates it
   * shares with its siblings. Rebuilt per cell rather than cached, since the row is half of it.
   */
  cellContext(row: T, column: Column<AppTableFeatures, T, unknown>): Record<string, unknown> {
    return { $implicit: row, ...(column.columnDef.meta?.templateContext ?? {}) }
  }

  classForRow(row: T): string | null {
    return this.rowClass === null ? null : this.rowClass(row)
  }

  get rowsAreActivatable(): boolean {
    return this.rowActivated.observed
  }

  /**
   * A press on something inside the row that is itself pressable — a button, a link, an option in the
   * row's own menu — belongs to that control, so only a press on the row itself opens it. The row
   * carries `role="button"` too, which is why the match has to be something other than the row.
   */
  activateRow(row: T, event: Event) {
    if (!this.rowsAreActivatable) {
      return
    }
    const target = event.target as HTMLElement | null
    const control = target?.closest('button, a, input, textarea, select, .checkbox, [role="button"]') ?? null
    if (control !== null && control !== event.currentTarget) {
      return
    }
    this.rowActivated.emit(row)
  }

  rowActionsPopover(row: T): string {
    return `${ROW_ACTIONS_POPOVER_PREFIX}${this.rowId(row)}`
  }

  // A row whose actions all fall away keeps its menu button out of the way rather than offering an
  // empty menu.
  rowActionsFor(row: T): AppTableRowAction<T>[] {
    return this.rowActions.filter((action) => action.isVisible === undefined || action.isVisible(row))
  }

  isRowActionDisabled(action: AppTableRowAction<T>, row: T): boolean {
    return action.isDisabled !== undefined && action.isDisabled(row)
  }

  rowActionLabel(action: AppTableRowAction<T>, row: T): string {
    const key = this.isRowActionDisabled(action, row) ? (action.disabledLabel ?? action.label) : action.label
    return this.translateService.instant(key)
  }

  runRowAction(action: AppTableRowAction<T>, row: T) {
    if (this.isRowActionDisabled(action, row)) {
      return
    }
    this.closePopover()
    this.rowActionTriggered.emit({ actionId: action.id, row })
  }

  chipFor(cell: Cell<AppTableFeatures, T, unknown>): AppTableChip | null {
    const chip = cell.column.columnDef.meta?.chip ?? null
    return chip === null ? null : chip(cell.row.original)
  }

  isActionDisabled(action: AppTableAction<T>): boolean {
    return this.actionScopedToSelection(action) && this.selectedCount === 0
  }

  actionLabel(action: AppTableAction<T>): string {
    const label = this.translateService.instant(action.label)
    return this.actionScopedToSelection(action) ? `${label} (${this.selectedCount})` : label
  }

  runAction(action: AppTableAction<T>) {
    const rows = this.selectedRows()
    if (this.actionScopedToSelection(action) && rows.length === 0) {
      return
    }
    this.closePopover()
    if (action.confirm === undefined) {
      this.actionTriggered.emit({ actionId: action.id, rows })
      return
    }
    const { title, description, confirmLabel } = action.confirm(rows)
    this.dialog
      .open(ConfirmDialogComponent, { data: { title, description, confirmLabel } })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed === true) {
          this.actionTriggered.emit({ actionId: action.id, rows })
        }
      })
  }
}
